import { useEffect, useMemo, useState } from "react";
import { api, BACKEND_URL } from "../lib/api";
import CrudTable from "./CrudTable";
import { Modal, FileCCE } from "./ui";
import MonthlyReadings, { MONTH_KEYS } from "./MonthlyReadings";
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { Plus, X, Save, Download } from "lucide-react";

const WASTE_TYPES = ["RP", "RME", "RSU", "Otro"];
const UNITS = ["M³", "KG"];
const STATUSES = ["En revisión", "Aprobado", "Rechazado"];
const ORIGIN_TYPES = ["Plataforma", "Embarcación", "Instalación terrestre", "Otro"];

export default function PemexVolumeValidation() {
  const [selected, setSelected] = useState(null);
  return (
    <div data-testid="pemex-volumes-module">
      <PemexVolumesDashboard />
      <CrudTable
        collection="pemex_volumes"
        testIdPrefix="pemex_volumes"
        onRowClick={(row) => setSelected(row)}
        fields={[
          { key: "code", label: "# Validación", readOnly: true },
          { key: "request_date", label: "Fecha solicitud", type: "date", required: true },
          { key: "waste_type", label: "Tipo de residuo", type: "select", options: WASTE_TYPES, required: true },
          { key: "waste_type_other", label: "Tipo (otro)" },
          { key: "origin", label: "Origen", type: "select", options: ORIGIN_TYPES },
          { key: "origin_name", label: "Nombre específico (Plataforma/Embarcación)" },
          { key: "volume_requested", label: "Vol. solicitado", type: "number" },
          { key: "volume_unit", label: "Unidad", type: "select", options: UNITS, default: "M³" },
          { key: "manifest_ref", label: "Manifiesto referencia" },
          { key: "shipping_notice", label: "Aviso desembarque" },
          { key: "provider_name", label: "Proveedor" },
          { key: "status", label: "Status", type: "select", options: STATUSES, default: "En revisión" },
        ]}
      />
      <Modal open={!!selected} onClose={() => setSelected(null)} title={`Validación de Volúmenes — ${selected?.code || ""}`} size="xl" testId="pemex-volume-detail">
        {selected && <VolumeDetail volume={selected} onClose={() => setSelected(null)} />}
      </Modal>
    </div>
  );
}

function VolumeDetail({ volume, onClose }) {
  const [tab, setTab] = useState("general");
  const [form, setForm] = useState({ ...volume });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    const payload = { ...form };
    delete payload.id; delete payload.tenant_id; delete payload.created_at; delete payload.updated_at; delete payload._id;
    try {
      await api.patch(`/data/pemex_volumes/${volume.id}`, payload);
      alert("Guardado.");
    } catch (e) {
      alert("Error: " + e.message);
    } finally { setSaving(false); }
  }

  return (
    <div>
      <div className="tabs-bar">
        {[
          ["general", "Datos del servicio"],
          ["provider", "Proveedor y operación"],
          ["monthly", "Volúmenes mensuales"],
          ["evidence", "Evidencias (CCE)"],
        ].map(([k, l]) => (
          <button key={k} className={`tab ${tab === k ? "active" : ""}`} onClick={() => setTab(k)} data-testid={`pv-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "general" && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Fecha de solicitud" type="date" v={form.request_date} on={(v) => setForm({ ...form, request_date: v })} testId="pv-request-date" />
          <Field label="Tipo de residuo" type="select" options={WASTE_TYPES} v={form.waste_type} on={(v) => setForm({ ...form, waste_type: v })} testId="pv-waste-type" />
          <Field label="Tipo (otro — si aplica)" v={form.waste_type_other} on={(v) => setForm({ ...form, waste_type_other: v })} testId="pv-waste-type-other" />
          <Field label="Origen" type="select" options={ORIGIN_TYPES} v={form.origin} on={(v) => setForm({ ...form, origin: v })} testId="pv-origin" />
          {form.origin === "Instalación terrestre" ? (
            <InstallationPicker
              v={form.origin_name}
              on={(v) => setForm({ ...form, origin_name: v })}
              testId="pv-installation"
            />
          ) : (form.origin === "Plataforma" || form.origin === "Embarcación") ? (
            <Field label={`Nombre de ${form.origin}`} v={form.origin_name} on={(v) => setForm({ ...form, origin_name: v })} testId="pv-origin-name" />
          ) : null}
          <div className="grid grid-cols-2 gap-2">
            <Field label="Volumen solicitado" type="number" v={form.volume_requested} on={(v) => setForm({ ...form, volume_requested: Number(v) })} testId="pv-vol-req" />
            <Field label="Unidad" type="select" options={UNITS} v={form.volume_unit || "M³"} on={(v) => setForm({ ...form, volume_unit: v })} testId="pv-unit" />
          </div>
          <Field label="Manifiesto referencia" v={form.manifest_ref} on={(v) => setForm({ ...form, manifest_ref: v })} testId="pv-manifest" />
          <Field label="Aviso de desembarque" v={form.shipping_notice} on={(v) => setForm({ ...form, shipping_notice: v })} testId="pv-shipping" />
          <Field label="Status" type="select" options={STATUSES} v={form.status} on={(v) => setForm({ ...form, status: v })} testId="pv-status" />
        </div>
      )}

      {tab === "provider" && <ProviderTab form={form} setForm={setForm} />}
      {tab === "monthly" && (
        <MonthlyReadings
          collection="pemex_volume_monthly"
          title={`Seguimiento mensual — ${volume.code || volume.id}`}
          filters={{ pemex_volume_id: volume.id }}
          numericKeys={[
            { key: "collected", label: `Volumen recolectado (${form.volume_unit || "M³"})` },
            { key: "disposed", label: `Volumen dispuesto (${form.volume_unit || "M³"})` },
            { key: "valorized", label: `Volumen revalorizado (${form.volume_unit || "M³"})` },
          ]}
          headerCards={(_row, ytd) => ([
            { label: "Recolectado YTD", value: (ytd.collected || 0).toLocaleString("es-MX") },
            { label: "Dispuesto YTD", value: (ytd.disposed || 0).toLocaleString("es-MX") },
            { label: "Revalorizado YTD", value: (ytd.valorized || 0).toLocaleString("es-MX") },
          ])}
          chart={{
            title: "Evolución mensual",
            series: [
              { key: "collected", label: "Recolectado", color: "#1d9e75" },
              { key: "disposed", label: "Dispuesto", color: "#e74c3c" },
              { key: "valorized", label: "Revalorizado", color: "#f1c40f" },
            ],
          }}
        />
      )}
      {tab === "evidence" && (
        <FileCCE module="pemex_volumes" parentId={volume.id} />
      )}

      {tab !== "monthly" && tab !== "evidence" && (
        <div className="flex justify-end gap-2 mt-3">
          <button className="btn" onClick={onClose}>Cerrar</button>
          <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="pv-save">
            {saving ? <span className="spinner" /> : <><Save size={14} /> Guardar</>}
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, type = "text", options, v, on, testId }) {
  return (
    <div>
      <label className="label">{label}</label>
      {type === "select" ? (
        <select className="select" value={v ?? ""} onChange={(e) => on(e.target.value)} data-testid={testId}>
          <option value="">--</option>
          {(options || []).map((o) => <option key={o}>{o}</option>)}
        </select>
      ) : type === "textarea" ? (
        <textarea className="textarea" rows={3} value={v ?? ""} onChange={(e) => on(e.target.value)} data-testid={testId} />
      ) : (
        <input className="input" type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} data-testid={testId} />
      )}
    </div>
  );
}

function ProviderTab({ form, setForm }) {
  const [providers, setProviders] = useState([]);
  useEffect(() => {
    api.get("/data/providers").then(({ data }) => setProviders(data)).catch(() => {});
  }, []);

  const employees = Array.isArray(form.employees_approved) ? form.employees_approved : [];

  function addEmployee() {
    const name = window.prompt("Nombre del empleado/chofer aprobado:");
    if (!name) return;
    setForm({ ...form, employees_approved: [...employees, name.trim()] });
  }
  function removeEmployee(idx) {
    const copy = [...employees]; copy.splice(idx, 1);
    setForm({ ...form, employees_approved: copy });
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="Proveedor (texto libre)" v={form.provider_name} on={(v) => setForm({ ...form, provider_name: v })} testId="pv-provider-name" />
      <div>
        <label className="label">Proveedor del catálogo</label>
        <select className="select" value={form.provider_id || ""} onChange={(e) => {
          const id = e.target.value;
          const p = providers.find((x) => x.id === id);
          setForm({ ...form, provider_id: id, provider_name: p?.name || form.provider_name, provider_auth_number: p?.authorization_number || form.provider_auth_number });
        }} data-testid="pv-provider-id">
          <option value="">--</option>
          {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <Field label="# Autorización del proveedor" v={form.provider_auth_number} on={(v) => setForm({ ...form, provider_auth_number: v })} testId="pv-auth-num" />
      <Field label="Embarcación que recolecta" v={form.vessel} on={(v) => setForm({ ...form, vessel: v })} testId="pv-vessel" />
      <Field label="Muelle de llegada" v={form.dock} on={(v) => setForm({ ...form, dock: v })} testId="pv-dock" />
      <Field label="Sitio de disposición final" v={form.disposal_site} on={(v) => setForm({ ...form, disposal_site: v })} testId="pv-disposal-site" />

      <div className="col-span-2">
        <label className="label">Empleados / choferes aprobados</label>
        <div className="flex flex-wrap gap-2 mt-2" data-testid="pv-employees-list">
          {employees.map((e, i) => (
            <span key={i} className="badge" style={{ background: "var(--bg-2)", padding: "4px 8px" }}>
              {e}
              <button onClick={() => removeEmployee(i)} className="ml-2" style={{ background: "none", border: 0, color: "var(--danger)", cursor: "pointer" }}>×</button>
            </span>
          ))}
          <button className="btn btn-sm btn-primary" onClick={addEmployee} data-testid="pv-add-employee"><Plus size={12} /> Agregar</button>
        </div>
      </div>
    </div>
  );
}


// ----- Dashboard de Volúmenes (BLOQUE 4) -----
const MONTHS_LBL = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const TYPE_COLORS = { RP: "#1d9e75", RME: "#3498db", RSU: "#f1c40f", Otro: "#95a5a6" };

export function PemexVolumesDashboard() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [typeFilter, setTypeFilter] = useState([]);
  const [originFilter, setOriginFilter] = useState([]);
  const [monthFilter, setMonthFilter] = useState("");
  const [volumes, setVolumes] = useState([]);
  const [monthlyByVol, setMonthlyByVol] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data: vols } = await api.get("/data/pemex_volumes");
        setVolumes(vols);
        const monthly = {};
        for (const v of vols) {
          const { data: m } = await api.get("/data/pemex_volume_monthly", { params: { pemex_volume_id: v.id } });
          monthly[v.id] = (m || []).filter((r) => r.year === year);
        }
        setMonthlyByVol(monthly);
      } catch (e) { console.error(e); }
    })();
  }, [year]);

  const filtered = useMemo(() => volumes.filter((v) => {
    if (typeFilter.length && !typeFilter.includes(v.waste_type)) return false;
    if (originFilter.length && !originFilter.includes(v.origin)) return false;
    return true;
  }), [volumes, typeFilter, originFilter]);

  function sumByMonth(field) {
    const months = [0,0,0,0,0,0,0,0,0,0,0,0];
    filtered.forEach((v) => {
      (monthlyByVol[v.id] || []).forEach((r) => {
        MONTH_KEYS.forEach((mk, i) => {
          months[i] += Number(r?.[`${field}_${mk}`] || 0) || 0;
        });
      });
    });
    return months;
  }

  const collectedM = sumByMonth("collected");
  const disposedM = sumByMonth("disposed");
  const valorizedM = sumByMonth("valorized");
  const monthIdx = monthFilter === "" ? null : MONTH_KEYS.indexOf(monthFilter);
  const ytdOf = (arr) => monthIdx == null ? arr.reduce((a, b) => a + b, 0) : (arr[monthIdx] || 0);

  const chart1Data = MONTHS_LBL.map((m, i) => {
    const row = { month: m };
    ["RP","RME","RSU","Otro"].forEach((t) => {
      const total = filtered.filter((v) => v.waste_type === t).reduce((a, v) =>
        a + (monthlyByVol[v.id] || []).reduce((b, r) => b + Number(r?.[`collected_${MONTH_KEYS[i]}`] || 0), 0), 0);
      row[t] = round2(total);
    });
    return row;
  });

  const allOrigins = Array.from(new Set(filtered.map((v) => v.origin).filter(Boolean)));
  const chart2Data = MONTHS_LBL.map((m, i) => {
    const row = { month: m };
    allOrigins.forEach((o) => {
      const total = filtered.filter((v) => v.origin === o).reduce((a, v) =>
        a + (monthlyByVol[v.id] || []).reduce((b, r) => b + Number(r?.[`collected_${MONTH_KEYS[i]}`] || 0), 0), 0);
      row[o] = round2(total);
    });
    return row;
  });
  const originColors = ["#1d9e75", "#3498db", "#e67e22", "#9b59b6", "#e74c3c", "#f1c40f"];

  const chart3Data = MONTHS_LBL.map((m, i) => ({
    month: m,
    Recolectado: round2(collectedM[i]),
    Dispuesto: round2(disposedM[i]),
    Revalorizado: round2(valorizedM[i]),
  }));

  const summaryRows = useMemo(() => {
    const byOrigin = {};
    filtered.forEach((v) => {
      const key = v.origin || "Sin origen";
      if (!byOrigin[key]) byOrigin[key] = { origin: key, count: 0, collected: 0, disposed: 0, valorized: 0 };
      byOrigin[key].count += 1;
      (monthlyByVol[v.id] || []).forEach((r) => {
        MONTH_KEYS.forEach((mk) => {
          byOrigin[key].collected += Number(r?.[`collected_${mk}`] || 0);
          byOrigin[key].disposed += Number(r?.[`disposed_${mk}`] || 0);
          byOrigin[key].valorized += Number(r?.[`valorized_${mk}`] || 0);
        });
      });
    });
    return Object.values(byOrigin).map((r) => ({
      ...r,
      pct_valorized: r.collected ? round2((r.valorized / r.collected) * 100) : 0,
    }));
  }, [filtered, monthlyByVol]);

  function toggleArr(arr, val, setter) {
    setter(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  }

  function exportSummaryXlsx() {
    const headers = ["Origen","Registros","Recolectado","Dispuesto","Revalorizado","% Revalorizado"];
    const rows = summaryRows.map((r) => [r.origin, r.count, r.collected.toFixed(2), r.disposed.toFixed(2), r.valorized.toFixed(2), r.pct_valorized.toFixed(2)]);
    const csv = "\ufeff" + [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `pemex_volumes_summary_${year}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="card mb-3" data-testid="pemex-volumes-dashboard">
      <div className="card-title flex items-center justify-between flex-wrap gap-2">
        <span>Dashboard de Volúmenes PEMEX</span>
        <div className="flex gap-2 flex-wrap">
          <select className="select" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="pvd-year">
            {[currentYear, currentYear-1, currentYear-2, currentYear-3].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select className="select" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} data-testid="pvd-month">
            <option value="">Todos los meses</option>
            {MONTH_KEYS.map((mk, i) => <option key={mk} value={mk}>{MONTHS_LBL[i]}</option>)}
          </select>
          <button className="btn btn-sm" onClick={exportSummaryXlsx} data-testid="pvd-export"><Download size={12} /> Exportar resumen</button>
        </div>
      </div>

      <div className="flex gap-3 mb-3 flex-wrap">
        <div>
          <div className="text-xs text-dim mb-1">Tipo residuo:</div>
          <div className="flex gap-1">
            {["RP","RME","RSU","Otro"].map((t) => (
              <button key={t} className={`btn btn-sm ${typeFilter.includes(t) ? "btn-primary" : ""}`} onClick={() => toggleArr(typeFilter, t, setTypeFilter)} data-testid={`pvd-type-${t}`}>{t}</button>
            ))}
          </div>
        </div>
        {allOrigins.length > 0 && (
          <div>
            <div className="text-xs text-dim mb-1">Origen:</div>
            <div className="flex gap-1 flex-wrap">
              {allOrigins.map((o) => (
                <button key={o} className={`btn btn-sm ${originFilter.includes(o) ? "btn-primary" : ""}`} onClick={() => toggleArr(originFilter, o, setOriginFilter)}>{o}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-4 gap-3 mb-3">
        <PvKpiBox label="Recolectado YTD" value={ytdOf(collectedM).toFixed(2)} />
        <PvKpiBox label="Dispuesto YTD" value={ytdOf(disposedM).toFixed(2)} />
        <PvKpiBox label="Revalorizado YTD" value={ytdOf(valorizedM).toFixed(2)} />
        <PvKpiBox label="Registros activos" value={filtered.length} />
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <div className="card-title text-sm">Recolección por tipo de residuo</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chart1Data}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="var(--text-dim)" fontSize={11} interval={0} />
              <YAxis stroke="var(--text-dim)" fontSize={11} />
              <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {Object.entries(TYPE_COLORS).map(([t, c]) => <Bar key={t} dataKey={t} fill={c} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div>
          <div className="card-title text-sm">Recolección por origen (apilado)</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chart2Data}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="var(--text-dim)" fontSize={11} interval={0} />
              <YAxis stroke="var(--text-dim)" fontSize={11} />
              <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {allOrigins.map((o, i) => <Bar key={o} dataKey={o} stackId="origin" fill={originColors[i % originColors.length]} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="mb-3">
        <div className="card-title text-sm">Trazabilidad: Recolectado vs Dispuesto vs Revalorizado</div>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chart3Data}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
            <XAxis dataKey="month" stroke="var(--text-dim)" fontSize={11} interval={0} />
            <YAxis stroke="var(--text-dim)" fontSize={11} />
            <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line dataKey="Recolectado" stroke="#1d9e75" strokeWidth={2} dot={false} />
            <Line dataKey="Dispuesto" stroke="#e74c3c" strokeWidth={2} dot={false} />
            <Line dataKey="Revalorizado" stroke="#f1c40f" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Origen</th><th>Registros</th><th>Recolectado</th><th>Dispuesto</th><th>Revalorizado</th><th>% Revalorizado</th></tr></thead>
          <tbody>
            {summaryRows.length === 0 ? (
              <tr><td colSpan={6} className="text-dim text-center">Sin datos para los filtros aplicados</td></tr>
            ) : summaryRows.map((r) => (
              <tr key={r.origin}>
                <td>{r.origin}</td>
                <td>{r.count}</td>
                <td>{r.collected.toFixed(2)}</td>
                <td>{r.disposed.toFixed(2)}</td>
                <td>{r.valorized.toFixed(2)}</td>
                <td>{r.pct_valorized.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function round2(n) { return Math.round((Number(n) || 0) * 100) / 100; }

// Item 13 — Installation picker with free text + persisted catalog
function InstallationPicker({ v, on, testId }) {
  const [list, setList] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  useEffect(() => {
    api.get("/data/tenant_config", { params: { key: "pemex_installations" } })
      .then(({ data }) => { if (data.length && Array.isArray(data[0].value)) setList(data[0].value); })
      .catch(() => {});
  }, []);
  async function save(newList) {
    const { data } = await api.get("/data/tenant_config", { params: { key: "pemex_installations" } });
    if (data.length) await api.patch(`/data/tenant_config/${data[0].id}`, { value: newList });
    else await api.post("/data/tenant_config", { key: "pemex_installations", value: newList });
  }
  async function addNew() {
    const name = draft.trim();
    if (!name) return;
    const next = Array.from(new Set([...list, name]));
    setList(next);
    on(name);
    setDraft(""); setAdding(false);
    save(next);
  }
  return (
    <div>
      <label className="label">Instalación terrestre</label>
      {adding ? (
        <div className="flex gap-2">
          <input className="input" autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addNew(); if (e.key === "Escape") setAdding(false); }} placeholder="Nombre de la instalación" data-testid={`${testId}-new`} />
          <button className="btn btn-sm btn-primary" onClick={addNew} data-testid={`${testId}-save`}>+ Agregar</button>
          <button className="btn btn-sm" onClick={() => setAdding(false)}>✕</button>
        </div>
      ) : (
        <div className="flex gap-2">
          <select className="select" value={v || ""} onChange={(e) => on(e.target.value)} data-testid={testId} style={{ flex: 1 }}>
            <option value="">-- Selecciona --</option>
            {list.map((i) => <option key={i}>{i}</option>)}
            {v && !list.includes(v) && <option value={v}>{v}</option>}
          </select>
          <button className="btn btn-sm btn-primary" onClick={() => setAdding(true)} data-testid={`${testId}-add-btn`}>+ Agregar instalación</button>
        </div>
      )}
    </div>
  );
}

function PvKpiBox({ label, value }) {
  return <div className="kpi-card"><div className="label">{label}</div><div className="value text-lg">{value}</div></div>;
}

